import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const NotificationPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [viewNotification, setViewNotification] = useState(null);
  const [editingNotification, setEditingNotification] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link: '',
    notificationImage: null,
  });
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);  

  useEffect(() => {
    fetchNotifications(currentPage, searchQuery);
  }, [currentPage, searchQuery]);

  const fetchNotifications = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const res = await axios.get(`/admin/api/notifications?page=${page}&limit=${limit}&search=${search}`);
      const { notifications = [], totalCount = 0 } = res.data;
      setNotifications(notifications);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch Notifications:', err);
      toastError('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation for required fields
    const validationErrors = validateForm(formData, {
      title: { required: true },
      description: { required: true },
    });

    // Additional validation for image file
    if (formData.notificationImage instanceof File) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      
      if (formData.notificationImage.size > maxSize) {
        validationErrors.notificationImage = 'Image size should be less than 5MB';
      }
      
      if (!allowedTypes.includes(formData.notificationImage.type)) {
        validationErrors.notificationImage = 'Only JPEG, PNG, GIF, and WebP images are allowed';
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const formDataToSend = new FormData();
      
      // Required fields
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      
      // Optional fields
      if (formData.link) {
        formDataToSend.append('link', formData.link);
      }
      
      if (formData.notificationImage instanceof File) {
        formDataToSend.append('notificationImage', formData.notificationImage);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (editingNotification) {
        await axios.put(`/admin/api/notifications/${editingNotification._id}`, formDataToSend, config);
        toastSuccess('Notification updated successfully!');
      } else {
        await axios.post('/admin/api/notifications', formDataToSend, config);
        toastSuccess('Notification added successfully!');
      }
      setCurrentPage(1);
      fetchNotifications(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error('Failed to save Notification:', err);
      toastError(err.response?.data?.message || 'Failed to save notification');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (notification) => {
    setEditingNotification(notification);
    setFormData({
      title: notification.title || '',
      description: notification.description || '',
      link: notification.link || '',
      notificationImage: null, // Don't preload file for editing
    });
    setImagePreview(notification.notificationImage || '');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this notification?')) {
      try {
        await axios.delete(`/admin/api/notifications/${id}`);
        toastSuccess('Notification deleted successfully!');
        setCurrentPage(1);
        fetchNotifications(1, searchQuery);
      } catch (err) {
        console.error('Failed to delete Notification:', err);
        toastError('Failed to delete notification');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormData({ 
      title: '',
      description: '',
      link: '',
      notificationImage: null,
    });
    setImagePreview('');
    setEditingNotification(null);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, notificationImage: file }));
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview('');
    }
  };

  const handleViewNotification = (notification) => {
    setViewNotification(notification);
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Notification</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search notifications..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Notification List</h5>
          {/* <button
                      className="btn btn-sm btn-success"
                      onClick={() => setShowModal(true)}
                    >
                      🔔 Add Notification
                    </button> */}
        </div>
       <div className="table-responsive">
        <div className="card-body p-0">
          <table
            className="table table-bordered table-sm mb-0"
            style={{ fontSize: '14px', lineHeight: '2' }}
          >
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr no.</th>
                <th className="py-1 px-2">Module name</th>
                <th className="py-1 px-2">Action</th>
                <th className="py-1 px-2">Action By ( User name )</th>
                <th className="py-1 px-2">Date & time</th>
                <th className="py-1 px-2">What action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : notifications.length > 0 ? (
                notifications.map((notification, idx) => (
                  <tr key={notification._id}>
                    <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                    <td className="py-1 px-2">
                      <strong style={{ color: '#F94F01' }}>
                        {notification.title || '-'}
                      </strong>
                    </td>
                    <td className="py-1 px-2">
                      <span style={{ fontSize: '12px' }}>
                        {notification.description && notification.description.length > 40 
                          ? notification.description.substring(0, 40) + '...' 
                          : notification.description || '-'}
                      </span>
                      {notification.description && notification.description.length > 40 && (
                        <button 
                          className="btn btn-link p-0 ms-1"
                          style={{ fontSize: '11px', textDecoration: 'none' }}
                          onClick={() => handleViewNotification(notification)}
                        >
                          Read More
                        </button>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      {notification.link ? (
                        <a 
                          href={notification.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary text-decoration-none"
                          style={{ fontSize: '12px' }}
                          title={notification.link}
                        >
                          {notification.link.length > 25 ? notification.link.substring(0, 25) + '...' : notification.link}
                        </a>
                      ) : (
                        <span className="text-muted">No Link</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      {notification.notificationImage ? (
                        <img 
                          src={notification.notificationImage} 
                          alt="Notification" 
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            objectFit: 'cover',
                            borderRadius: '50%',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(notification.notificationImage, '_blank')}
                          title="Click to view full image"
                        />
                      ) : (
                        <span 
                          className="badge"
                          style={{ 
                            backgroundColor: '#F94F01', 
                            color: 'white',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          🔔
                        </span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      {notification.createdAt ? (
                        <span style={{ fontSize: '12px' }}>
                          {new Date(notification.createdAt).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-2">No Notifications found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className="btn btn-sm"
                  style={{
                    backgroundColor: currentPage === page ? '#F94F01' : 'transparent',
                    color: currentPage === page ? 'white' : '#F94F01',
                    border: '1px solid #F94F01'
                  }}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
      {/* Add/Edit Notification Modal using CommonModalForm */}
      <CommonModalForm
        visible={showModal}
        title={editingNotification ? 'Edit Notification' : 'Add Notification'}
        fields={[
          { 
            name: 'title', 
            label: 'Title', 
            type: 'text',
            placeholder: 'Enter notification title (e.g., Important Update, New Event)',
            required: true,
            maxLength: 100,
            helpText: 'Notification headline'
          },
          { 
            name: 'description', 
            label: 'Description', 
            type: 'textarea',
            placeholder: 'Enter detailed notification description...',
            required: true,
            maxLength: 500,
            rows: 4,
            helpText: 'Detailed notification content'
          },
          { 
            name: 'link', 
            label: 'Link', 
            type: 'url',
            placeholder: 'https://example.com/more-info',
            helpText: 'Clickable URL for notifications (Optional)'
          },
        ]}
        values={formData}
        errors={errors}
        onChange={(updatedValues) => {
          if (updatedValues.notificationImage instanceof File) {
            handleImageChange({ target: { files: [updatedValues.notificationImage] } });
          } else if (updatedValues.notificationImage === null) {
            setFormData({ ...formData, notificationImage: null });
            setImagePreview('');
          } else {
            setFormData(updatedValues);
          }
        }}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={saving ? (editingNotification ? 'Updating...' : 'Saving...') : (editingNotification ? 'Update Notification' : 'Save Notification')}
        requiredFields={['title', 'description']}
        imagePreview={imagePreview}
        disabled={saving}
      />

      {/* View Notification Modal */}
      {viewNotification && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🔔 Notification Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setViewNotification(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-8">
                    <div className="mb-3">
                      <h6 className="text-primary mb-2">📝 Title:</h6>
                      <p className="fs-5 fw-bold">{viewNotification.title}</p>
                    </div>

                    <div className="mb-3">
                      <h6 className="text-primary mb-2">📄 Description:</h6>
                      <p className="mb-3" style={{ lineHeight: '1.6' }}>
                        {viewNotification.description}
                      </p>
                    </div>

                    {viewNotification.link && (
                      <div className="mb-3">
                        <h6 className="text-primary mb-2">🔗 Link:</h6>
                        <p className="mb-3">
                          <a 
                            href={viewNotification.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-decoration-none btn btn-outline-primary btn-sm"
                          >
                            🌐 Open Link
                          </a>
                        </p>
                      </div>
                    )}

                    <div className="row">
                      <div className="col-md-12">
                        <h6 className="text-primary mb-2">📅 Created:</h6>
                        <p className="mb-0">
                          {new Date(viewNotification.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    {viewNotification.notificationImage && (
                      <div>
                        <h6 className="text-primary mb-2">🖼️ Image:</h6>
                        <img 
                          src={viewNotification.notificationImage} 
                          alt="Notification" 
                          className="img-fluid rounded"
                          style={{ 
                            maxHeight: '200px', 
                            objectFit: 'cover',
                            border: '2px solid #5A4FCF',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(viewNotification.notificationImage, '_blank')}
                          title="Click to view full size"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setViewNotification(null)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{ backgroundColor: '#5A4FCF', color: 'white' }}
                  onClick={() => {
                    handleEdit(viewNotification);
                    setViewNotification(null);
                  }}
                >
                  ✏️ Edit Notification
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPage;
